import { IOfficeAccountingTableRow } from "@/src/types/officeAccounting";

/** Choose values in payment_fact_eur_records.invoice_number */
export const PAYMENT_FACT_EUR_INVOICE = {
  MISSED_CLASSES: "Missed Classes",
  NOTIFICATION: "Notification",
} as const;

/** Current Semester popup options: `1st semester` … `12th semester`. */
export const OFFICE_ACCOUNTING_CURRENT_SEMESTER_OPTIONS = [
  "1st semester",
  "2nd semester",
  "3rd semester",
  "4th semester",
  "5th semester",
  "6th semester",
  "7th semester",
  "8th semester",
  "9th semester",
  "10th semester",
  "11th semester",
  "12th semester",
] as const;

/** Cost of the Nst semester columns (index = semester number 1..12). */
export const OFFICE_ACCOUNTING_COST_SEMESTER_COLUMNS_BY_NUMBER = [
  "",
  "costFirstSemester",
  "costSecondSemester",
  "costThirdSemester",
  "costFouthSemester",
  "costFifthSemester",
  "costSixthSemester",
  "costSeventhSemester",
  "costEighthSemester",
  "costNinthSemester",
  "costTenthSemester",
  "costEleventhSemester",
  "costTwelfthSemester",
] as const;

const COST_SEMESTER_COLUMN_SET = new Set<string>(
  OFFICE_ACCOUNTING_COST_SEMESTER_COLUMNS_BY_NUMBER.filter(Boolean),
);

/** Parse `1st semester` / legacy values → 1..12, or null if unknown. */
export const parseOfficeAccountingCurrentSemesterNumber = (
  raw: string | null | undefined,
): number | null => {
  const text = String(raw ?? "").trim().toLowerCase();
  if (!text) return null;

  const explicit = text.match(/(\d{1,2})\s*(st|nd|rd|th)?\s*semester/);
  if (explicit) {
    const n = Number(explicit[1]);
    if (Number.isFinite(n) && n >= 1 && n <= 12) return n;
  }

  const anyNumber = text.match(/(\d{1,2})/);
  if (!anyNumber) return null;
  const n = Number(anyNumber[1]);
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : null;
};

/**
 * Cost semester columns included in Total Amount USD for Current Semester (cumulative):
 * - odd N  → Cost 1 … Cost (N+1)
 * - even N → Cost 1 … Cost N
 * - unset  → null (no filter)
 *
 * Examples:
 * - 1st → 1..2
 * - 2nd → 1..2
 * - 3rd → 1..4
 * - 4th → 1..4
 */
export const getCostSemesterColumnsForCurrentSemester = (
  currentSemester: string | null | undefined,
): ReadonlySet<string> | null => {
  const semester = parseOfficeAccountingCurrentSemesterNumber(currentSemester);
  if (semester == null) return null;

  const endSemester = semester % 2 === 1 ? Math.min(semester + 1, 12) : semester;
  const allowed = new Set<string>();
  for (let n = 1; n <= endSemester; n += 1) {
    const column = OFFICE_ACCOUNTING_COST_SEMESTER_COLUMNS_BY_NUMBER[n];
    if (column) allowed.add(column);
  }
  return allowed;
};

/** Columns summed into Total Amount USD (Preparatory Department → Hostel Accommodation). */
export const OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS = [
  "preparatoryDepartment",
  "costFirstSemester",
  "costSecondSemester",
  "costThirdSemester",
  "costFouthSemester",
  "costFifthSemester",
  "costSixthSemester",
  "costSeventhSemester",
  "costEighthSemester",
  "costNinthSemester",
  "costTenthSemester",
  "costEleventhSemester",
  "costTwelfthSemester",
  "pgPhdCostFirstSemester",
  "pgPhdCostSecondSemester",
  "pgPhdCostThirdSemester",
  "pgPhdCostFourthSemester",
  "pgPhdCostFifthSemester",
  "pgPhdCostSixthSemester",
  "otherExpenses",
  "transfer",
  "insurance",
  "admissionFee",
  "temporaryResidencePermission",
  "hostelAccommodation",
] as const;


export type OfficeAccountingTotalAmountUsdSourceColumn =
  (typeof OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS)[number];

const SOURCE_COLUMN_SET = new Set<string>(OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS);

export const isOfficeAccountingTotalAmountUsdSourceColumn = (columnId: string): boolean =>
  SOURCE_COLUMN_SET.has(columnId);

const stripCurrencyNoise = (raw: string): string =>
  raw
    .replace(/USD|EUR|UAH/gi, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .trim();

/** Parse display/API money strings like `1 200.00 USD`, `1200`, `=100+50`. */
export const parseOfficeAccountingAmount = (raw: string | null | undefined): number => {
  const original = String(raw ?? "").trim();
  if (!original || original === "—" || original === "-") return 0;

  const cleaned = stripCurrencyNoise(original.replace(/^=/, ""));
  if (!cleaned) return 0;

  if (/^[\d+\-*/.()]+$/.test(cleaned)) {
    try {
      const result = Function(`"use strict"; return (${cleaned})`)();
      return typeof result === "number" && Number.isFinite(result) ? result : 0;
    } catch {
      return 0;
    }
  }

  const match = cleaned.match(/-?\d+(\.\d+)?/);
  if (!match) return 0;
  const parsed = Number.parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatOfficeAccountingUsdAmount = (amount: number): string => {
  const safe = Number.isFinite(amount) ? amount : 0;
  const [intPart, decPart] = safe.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart} USD`;
};

export const formatOfficeAccountingEurAmount = (amount: number): string => {
  const safe = Number.isFinite(amount) ? amount : 0;
  const [intPart, decPart] = safe.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart} EUR`;
};

/** @deprecated Use formatOfficeAccountingUsdAmount */
export const formatOfficeAccountingTotalAmountUsd = formatOfficeAccountingUsdAmount;

const toAmountRaw = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.map(String).join(" ");
  return String(value);
};

export const computeOfficeAccountingTotalAmountUsd = (
  row: IOfficeAccountingTableRow,
  visibleColumnIds: ReadonlySet<string> | readonly string[],
): string => {
  const visible =
    visibleColumnIds instanceof Set ? visibleColumnIds : new Set(visibleColumnIds);
  const allowedCostSemesters = getCostSemesterColumnsForCurrentSemester(
    Array.isArray(row.currentSemester) ? row.currentSemester.join(" ") : row.currentSemester,
  );

  let sum = 0;
  for (const columnId of OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS) {
    const isCostSemester = COST_SEMESTER_COLUMN_SET.has(columnId);

    if (isCostSemester) {
      // Current Semester drives which Cost 1–12 columns enter the sum (even if column is hidden).
      if (allowedCostSemesters != null) {
        if (!allowedCostSemesters.has(columnId)) continue;
      } else if (!visible.has(columnId)) {
        // No Current Semester → keep previous behavior: only visible cost columns.
        continue;
      }
    } else if (!visible.has(columnId)) {
      continue;
    }

    sum += parseOfficeAccountingAmount(row[columnId as keyof IOfficeAccountingTableRow] as string);
  }

  return formatOfficeAccountingUsdAmount(sum);
};

/** Debt Balance USD = Total Amount USD − Total Paid USD */
export const computeOfficeAccountingDebtBalanceUsd = (row: IOfficeAccountingTableRow): string => {
  const totalAmount = parseOfficeAccountingAmount(row.totalAmountUsd);
  const totalPaid = parseOfficeAccountingAmount(toAmountRaw(row.totalPaidUsd || row.paymentFactUsd));
  return formatOfficeAccountingUsdAmount(totalAmount - totalPaid);
};

export const getOfficeAccountingTotalPaidUsdRaw = (row: IOfficeAccountingTableRow): string => {
  const fromPaymentFact = Array.isArray(row.paymentFactUsd)
    ? row.paymentFactUsd.join(" ")
    : (row.paymentFactUsd ?? "");
  if (fromPaymentFact) return fromPaymentFact;

  return Array.isArray(row.totalPaidUsd) ? row.totalPaidUsd.join(" ") : (row.totalPaidUsd ?? "");
};

export const getOfficeAccountingPaymentFactEurRaw = (row: IOfficeAccountingTableRow): string =>
  Array.isArray(row.paymentFactEur) ? row.paymentFactEur.join(" ") : (row.paymentFactEur ?? "");

/** Payment Fact EUR subset: Missed Classes / Notification (sum of result). */
export const getOfficeAccountingPaymentFactEurPaidByInvoice = (
  row: IOfficeAccountingTableRow,
  invoiceNumber: string,
): number =>
  (row.paymentFactEurRecords ?? []).reduce((sum, record) => {
    if ((record.invoice_number ?? "").trim() !== invoiceNumber) return sum;
    return sum + parseOfficeAccountingAmount(record.result);
  }, 0);

/** Balance Missed Classes EUR = Total Missed Classes EUR − Payment Fact EUR(Missed Classes) */
export const computeOfficeAccountingBalanceMissedClassesEur = (row: IOfficeAccountingTableRow): string => {
  const total = parseOfficeAccountingAmount(row.totalMissedClassesEur);
  const paid = getOfficeAccountingPaymentFactEurPaidByInvoice(row, PAYMENT_FACT_EUR_INVOICE.MISSED_CLASSES);
  return formatOfficeAccountingEurAmount(total - paid);
};

/** Balance Nostrifications EUR = Total Nostrifications EUR − Payment Fact EUR(Notification) */
export const computeOfficeAccountingBalanceNostrificationsEur = (row: IOfficeAccountingTableRow): string => {
  const total = parseOfficeAccountingAmount(row.totalNostrificationsEur);
  const paid = getOfficeAccountingPaymentFactEurPaidByInvoice(row, PAYMENT_FACT_EUR_INVOICE.NOTIFICATION);
  return formatOfficeAccountingEurAmount(total - paid);
};

export const applyOfficeAccountingDerivedUsdAmounts = <T extends IOfficeAccountingTableRow>(
  rows: T[],
  visibleColumnIds: ReadonlySet<string> | readonly string[],
): T[] =>
  rows.map((row) => {
    const totalAmountUsd = computeOfficeAccountingTotalAmountUsd(row, visibleColumnIds);
    const totalPaidUsd = getOfficeAccountingTotalPaidUsdRaw(row);
    return {
      ...row,
      totalAmountUsd,
      totalPaidUsd,
      debtBalanceUsd: computeOfficeAccountingDebtBalanceUsd({ ...row, totalAmountUsd, totalPaidUsd }),
    };
  });

/** @deprecated Use applyOfficeAccountingDerivedUsdAmounts */
export const applyOfficeAccountingTotalAmountUsd = applyOfficeAccountingDerivedUsdAmounts;
